// src/App.tsx
import React, { Suspense, useRef, useState, useMemo } from 'react'
import { SceneCanvas } from './scene/SceneCanvas.tsx'
import { SceneReadyWatcher } from './scene/SceneReady.tsx'
import { useSceneAnimation } from './scene/SceneAnimation.ts'
import { Loader } from './ui/Loader.tsx'
import { Configurator } from './ui/Configurator.tsx'
import { Hero } from './ui/HeroSection.tsx'
import { FeatureSection } from './ui/FeatureSection.tsx'
import { useIsMobile } from './hooks/useIsMobile.ts'
import { usePricing } from './hooks/usePricing.ts'
import { Menu } from './ui/Menu.tsx'
import { Footer } from './ui/Footer.tsx'

export default function App() {
  const mainRef = useRef<HTMLDivElement>(null)
  const modelRef = useRef<any>(null)
  const cameraRef = useRef<any>(null)
  const controlsRef = useRef<any>(null)
  const fabricRef = useRef<any>(null)

  const isMobile = useIsMobile()
  const [loadingDone, setLoadingDone] = useState(false)
  const [largeur, setLargeur] = useState(180)
  const [computePrice, setComputePrice] = useState(4500)
  const [optionProgression, setOptionProgression] = useState(false)

  const [textileType, setTextileType] = useState<'denim' | 'maille' | 'similicuir'>('denim')
  const [activeTexture, setActiveTexture] = useState<string>('7914.jpg') // default

  const { nbCam, eur, tnd } = usePricing(largeur, computePrice, optionProgression)
  useSceneAnimation(loadingDone, mainRef, modelRef, cameraRef, controlsRef, isMobile, fabricRef)

  // --- TEXTURES PUBLIC ---
  const textureList = [
    '2547.jpg','283.jpg','4589.jpg','4612.jpg','4977.jpg','4978.jpg',
    '5565.jpg','5579.jpg','6741.jpg','7021.jpg','7387.jpg','7786.jpg',
    '7924.jpg','7998.jpg','8930.jpg','9297.jpg','9393.jpg','9471.jpg',
    '9478.jpg','9760.jpg'
  ]

  const texturePathMap = useMemo(() => {
    const base = `${process.env.PUBLIC_URL}/textures/`
    const map: Record<string, string> = {}
    textureList.forEach(tex => { map[tex] = `${base}${tex}` })
    return map
  }, [textureList])

  // --- Menu Links ---
  const menuLinks = [
    
    { label: 'Textiles', href: '#feature' },
    { label: 'Configurateur', href: '#configurator' },
    // { label: 'Contact', href: '#footer' }
    { label: 'Accueil', href: '#hero' },
  ]

  return (
    <div ref={mainRef} className="scroll-wrapper" style={{ background: '#f5f7f9', minHeight: '100vh', position: 'relative' }}>
      {/* ===== Menu fixe ===== */}
      <Menu links={menuLinks} isMobile={isMobile} />

      {/* ===== Loader ===== */}
      {!loadingDone && <Loader onFinished={() => setLoadingDone(true)} />}

      <Suspense fallback={<Loader />}>
        <SceneReadyWatcher onReady={() => setLoadingDone(true)} />

        {/* ===== 3D BACKGROUND ===== */}
        <div style={{ position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none' }}>
          <SceneCanvas
            cameraRef={cameraRef}
            controlsRef={controlsRef}
            modelRef={modelRef}
            isMobile={isMobile}
            largeur={largeur}
            nbCam={nbCam}
            textile={textileType}
            texture={activeTexture}
          />
        </div>

        {/* ===== UI CONTENT ===== */}
        <div style={{
          position: 'relative',
          zIndex: 10,
          opacity: loadingDone ? 1 : 0,
          transition: 'opacity 0.8s ease-in-out',
          pointerEvents: 'all'
        }}>

          {/* Hero Section */}
          <div id="hero">
            <Hero
              isMobile={isMobile}
              largeur={largeur}
              setLargeur={setLargeur}
              nbCam={nbCam}
            />
          </div>

          {/* Feature Section */}
          <div id="feature">
            <FeatureSection
              selectedTexture={activeTexture}
              onSelectTexture={(tex) => {
                setActiveTexture(tex)
                // Optionnel : si tex correspond à un preset textile, on peut aussi changer textileType
                if (tex.includes('12722')) setTextileType('maille')
                if (tex.includes('11081')) setTextileType('similicuir')
                if (tex.includes('7914')) setTextileType('denim')
              }}
            >
              {/* Boutons textile */}
              <div className="textile-button-container">
                {['denim', 'maille', 'similicuir'].map((t) => (
                  <button
                    key={t}
                    className={`textile-btn ${textileType === t ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      setTextileType(t as any)
                      // Optionnel : définir une texture de base quand on choisit un textile
                      if (t === 'denim') setActiveTexture('7914.jpg')
                      if (t === 'maille') setActiveTexture('12722.jpg')
                      if (t === 'similicuir') setActiveTexture('11081.jpg')
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </FeatureSection>
          </div>

          {/* Configurator Section */}
          <section id="configurator" style={{ height: '120vh', display: 'flex', alignItems: 'center', padding: '5% 5%', pointerEvents: 'none' }}>
            <Configurator
              largeur={largeur} setLargeur={setLargeur}
              computePrice={computePrice} setComputePrice={setComputePrice}
              optionProgression={optionProgression} setOptionProgression={setOptionProgression}
              nbCam={nbCam} eur={eur} tnd={tnd}
              isMobile={isMobile}
            />
          </section>

          {/* Footer */}
          <Footer
            companyName="ViZmerald"
            year={2026}
          />
        </div>
      </Suspense>
    </div>
  )
}
